import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import '../styles/SenhaMensal.css'
import { bff, BffError } from '../lib/bff'
import { LampContainer } from '@/components/ui/lamp'

interface SenhaResult {
  senha: string
  nomeRazaosocial: string
  dataInicial: string
  dataFinal: string
  liberadoPor: string
  dataLiberacao: string
}

type Estado =
  | { tipo: 'idle' }
  | { tipo: 'carregando' }
  | { tipo: 'resultado'; dados: SenhaResult }
  | { tipo: 'erro'; mensagem: string }

function formatarData(data: string): string {
  if (!data) return ''
  const match = data.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2})/)
  if (match) return `${match[3]}/${match[2]}/${match[1]} ${match[4]}`
  const matchDate = data.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (matchDate) return `${matchDate[3]}/${matchDate[2]}/${matchDate[1]}`
  return data
}

function formatarCNPJ(valor: string) {
  const digits = valor.replace(/\D/g, '').slice(0, 14)
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

const PASSOS = [
  { titulo: 'Informe o CNPJ', desc: 'Digite o CNPJ da empresa cadastrado no Sistema Quantum.' },
  { titulo: 'Consulte a senha', desc: 'A senha do período liberado aparece logo abaixo do formulário.' },
  { titulo: 'Copie e use', desc: 'Copie a senha com um clique para liberar o acesso ao sistema.' },
]

export default function SenhaMensal() {
  const [cnpj, setCnpj]       = useState('')
  const [estado, setEstado]   = useState<Estado>({ tipo: 'idle' })
  const [copiado, setCopiado] = useState(false)

  async function copiarSenha(senha: string) {
    try {
      await navigator.clipboard.writeText(senha)
    } catch {
      // Fallback para navegadores sem Clipboard API (ex.: página servida sem HTTPS)
      const textarea = document.createElement('textarea')
      textarea.value = senha
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const cnpjLimpo = cnpj.replace(/\D/g, '')
    if (cnpjLimpo.length !== 14) {
      setEstado({ tipo: 'erro', mensagem: 'Informe um CNPJ válido (14 dígitos).' })
      return
    }
    setEstado({ tipo: 'carregando' })
    setCopiado(false)
    try {
      const dados = await bff.get<SenhaResult>(`/validarsistema/${cnpjLimpo}`)
      setEstado({ tipo: 'resultado', dados })
    } catch (err) {
      if (err instanceof BffError && err.status === 404) {
        setEstado({ tipo: 'erro', mensagem: 'Nenhum resultado encontrado para este CNPJ.' })
      } else {
        setEstado({ tipo: 'erro', mensagem: 'Erro ao consultar. Tente novamente ou entre em contato conosco.' })
      }
    }
  }

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* ══ HERO ══ */}
      <LampContainer
        className="min-h-[22rem] md:min-h-[25rem] rounded-none pt-36 md:pt-44"
        lampClassName="translate-y-10"
        contentClassName="-translate-y-32"
        overlay={
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(26, 62, 115, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(26, 62, 115, 0.15) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
        }
      >
        <motion.h1
          initial={{ opacity: 0.5, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          className="mt-8 bg-gradient-to-br from-slate-300 to-slate-500 py-4 bg-clip-text text-center text-3xl font-medium tracking-tight text-transparent sm:text-4xl md:text-7xl px-4"
        >
          Senha Mensal
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.45,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          className="mt-2 max-w-xl text-center text-base text-slate-400 px-4"
        >
          Consulte a senha mensal de acesso ao Sistema Quantum informando seu CNPJ.
        </motion.p>
      </LampContainer>

      {/* ══ CORPO ══ */}
      <div className="sm-body">
        <div className="sm-body-inner">

          {/* ── Consulta ── */}
          <div className="sm-card">
            <div className="sm-card-head">
              <div className="sm-card-icon">
                <i className="fa-solid fa-key" aria-hidden="true" />
              </div>
              <div>
                <h2 className="sm-card-title">Consultar senha</h2>
                <p className="sm-card-sub">
                  Informe o CNPJ cadastrado no sistema. Dúvidas? Ligue <strong>(63)&nbsp;98404-2819</strong>
                </p>
              </div>
            </div>

            <form className="sm-form" onSubmit={handleSubmit}>
              <label htmlFor="cnpj" className="sm-label">CNPJ</label>
              <div className="sm-input-wrap">
                <i className="fa-regular fa-building" aria-hidden="true" />
                <input
                  id="cnpj"
                  name="cnpj"
                  className="sm-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                  value={cnpj}
                  onChange={e => setCnpj(formatarCNPJ(e.target.value))}
                />
              </div>
              <button
                type="submit"
                className="sm-btn-submit"
                disabled={estado.tipo === 'carregando'}
              >
                {estado.tipo === 'carregando' ? (
                  <><i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />Consultando…</>
                ) : (
                  <><i className="fa-solid fa-magnifying-glass" aria-hidden="true" />Consultar Senha</>
                )}
              </button>
            </form>

            {/* Erro */}
            {estado.tipo === 'erro' && (
              <div className="sm-alert-error" role="alert">
                <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
                <span>{estado.mensagem}</span>
              </div>
            )}

            {/* Resultado: nome, período e senha, nesta ordem */}
            {estado.tipo === 'resultado' && (
              <div className="sm-result" aria-live="polite">
                <div className="sm-result-item">
                  <span className="sm-result-label">Nome/Razão Social</span>
                  <span className="sm-result-empresa">{estado.dados.nomeRazaosocial || '—'}</span>
                </div>

                <div className="sm-result-item">
                  <span className="sm-result-label">Período de liberação</span>
                  <span className="sm-result-periodo">
                    <i className="fa-regular fa-calendar" aria-hidden="true" />
                    {formatarData(estado.dados.dataInicial)}
                    <span className="sm-result-ate">até</span>
                    {formatarData(estado.dados.dataFinal)}
                  </span>
                </div>

                <div className="sm-senha">
                  <span className="sm-senha-label">Senha de liberação</span>
                  <div className="sm-senha-digitos" aria-label={`Senha ${estado.dados.senha}`}>
                    {estado.dados.senha.split('').map((d, i) => (
                      <span key={i} className="sm-senha-digito" aria-hidden="true">{d}</span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={`sm-copy-btn${copiado ? ' copied' : ''}`}
                    onClick={() => copiarSenha(estado.dados.senha)}
                  >
                    <i className={`fa-solid ${copiado ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
                    {copiado ? 'Senha copiada' : 'Copiar senha'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Lateral ── */}
          <aside className="sm-lateral">
            <div className="sm-side-card">
              <h2 className="sm-side-title">Como funciona</h2>
              <ol className="sm-passos">
                {PASSOS.map((p, i) => (
                  <li key={p.titulo}>
                    <span className="sm-passo-num">{i + 1}</span>
                    <div>
                      <strong>{p.titulo}</strong>
                      <p>{p.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="sm-side-card sm-side-card--escuro">
              <div className="sm-side-icon">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
              </div>
              <h2 className="sm-side-title">Sua senha expirou?</h2>
              <p className="sm-side-text">
                A senha mensal é liberada automaticamente após a confirmação do pagamento do boleto.
                Caso sua senha tenha expirado, entre em contato com nossa equipe pelo número
                {' '}<strong>(63)&nbsp;98404-2819</strong> para liberação.
              </p>
              <div className="sm-side-btns">
                <Link to="/imprimir-boleto" className="sm-side-btn sm-side-btn--red">
                  <i className="fa-solid fa-file-invoice" aria-hidden="true" />
                  Imprimir Boleto
                </Link>
                <a href="https://wa.me/5563984042819" target="_blank" rel="noreferrer" className="sm-side-btn">
                  <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                  Falar no WhatsApp
                </a>
              </div>
            </div>
          </aside>

        </div>
      </div>

    </div>
  )
}
